import { Box, Button, ClickAwayListener, Fade, MenuItem, MenuList, Paper, Popper, Skeleton, useScrollTrigger } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ProductService from 'services/product.service';
import { categoryChanged } from 'store/actions/search.actions';
import { useAppDispatch } from 'store/hooks';
import { Category } from 'types/category.types';
import { GTM, GTMElements, GTMLocations } from 'utils/analytics';

import { SEARCH } from '../../config/fe-routes';
import { useLanguage } from '../../hooks/useLanguage';
import { redirectTo } from '../../utils/route-helpers';

const productService = new ProductService();

type Props = {
  GTMLocation?: GTMLocations;
};

const CategoryNav = (props: Props) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();
  const { language } = useLanguage();

  const { GTMLocation } = props;

  // Get categories - refetch when language changes
  useEffect(() => {
    (async () => {
      try {
        const receivedCategories = await productService.getCategories({ simplified: true, onlyCamelCased: true });

        setCategories(receivedCategories || []);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setCategories([]);
      }
    })();
  }, [language]);

  const openPopper = (e: React.MouseEvent<HTMLButtonElement>, category: Category) => {
    elementClicked(category.title);
    setAnchorEl(e.currentTarget);
    setOpen(category.id === selectedCategory?.id ? !open : true);
    setSelectedCategory(category);
  };

  // Handle GTM tags with item is clicked
  const elementClicked = (categoryTitle: string, subcategoryTitle?: string) => {
    GTM.elementClicked(
      GTMElements.CATEGORY_NAVBAR,
      GTMLocation,
      subcategoryTitle
        ? {
            element_sub_location: categoryTitle,
            element_subsub_location: subcategoryTitle,
          }
        : {
            element_sub_location: categoryTitle,
          },
    );
  };

  // Handle GTM and redirect if item is clicked
  const directToNavItem = (category: Category, subcategory?: Category) => {
    if (subcategory) {
      elementClicked(category.title, subcategory.title);
      dispatch(categoryChanged({ id: subcategory.id, parentId: category.id }));
    } else {
      elementClicked(category.title);
      dispatch(categoryChanged({ id: category.id }));
    }

    redirectTo({
      ...SEARCH(''),
      query: {
        categories: subcategory ? subcategory.id : category.id,
        menuOpen: false,
      },
      hideQueryKeys: ['menuOpen'],
    });
  };

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState<Category | null>(null);
  const trigger = useScrollTrigger({ threshold: 50 });

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  // Handle keyboard navigation
  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  // Return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);

  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorEl?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  // Close dropdown on scroll down, otherwise it scrolls to the top of page on ClickAwayListener click
  useEffect(() => {
    trigger && open && handleToggle();
  }, [trigger, open]);

  return (
    <div className="page-wide">
      <Box className="category-nav" display="flex">
        {isLoading ? (
          <Box display="flex" gap={1}>
            {new Array(8).fill(null).map((_, i) => (
              <Skeleton variant="rounded" animation="wave" height={36} width={80} key={'skeleton-' + i} />
            ))}
          </Box>
        ) : (
          <>
            {categories.map((category, index) =>
              category.categories.length ? (
                <Button
                  key={index}
                  className="category-nav-button"
                  aria-controls={open && selectedCategory?.id === category.id ? 'composition-menu' : undefined}
                  aria-expanded={open && selectedCategory?.id === category.id ? 'true' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    openPopper(e, category);
                  }}
                >
                  {category?.title}
                </Button>
              ) : (
                <Button key={index} className="category-nav-button" onClick={() => directToNavItem(category)}>
                  {category.title}
                </Button>
              ),
            )}
          </>
        )}
      </Box>

      {/*
          Use Popper because it supports disablePortal which allows clicking on other elements if dropdown is open,
          like other dropdown links, searchbar, scroll down etc.
        */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        className="category-nav-popper"
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              {/* Use ClickAwayListener to close dropdown on clicks elsewhere  */}
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  className="category-nav-submenu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                >
                  {selectedCategory?.categories?.map((subcategory, index) => (
                    <MenuItem
                      key={index}
                      onClick={(e) => {
                        handleClose(e);
                        directToNavItem(selectedCategory, subcategory);
                      }}
                    >
                      {subcategory.title}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
};

export default CategoryNav;
